import { toValue, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { yardAssetKeys } from '@/composables/queryKeys';
import yardSpotService from '@satellite/services/yardSpot-service';

import type { UseQueryOptions } from '@tanstack/vue-query';
import type { ISpot, IWarehouse } from '@nova/core';
import type { MaybeRefOrGetter } from 'vue';

type SpotId = ISpot['id'];
type WarehouseId = IWarehouse['id'];

export function useSpotById<TQueryFnData = ISpot, TError = unknown, TData = TQueryFnData>(
  spotId: MaybeRefOrGetter<SpotId | null | undefined>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> & {
    enabled?: MaybeRefOrGetter<boolean>;
  }
) {
  return useQuery<TQueryFnData, TError, TData>({
    ...options,
    queryKey: computed(() => ['yard-spot', toValue(spotId)] as const),
    enabled: () => {
      const id = toValue(spotId);
      const externalEnabled = toValue(
        (options?.enabled as MaybeRefOrGetter<boolean> | undefined) ?? true
      );
      return externalEnabled && Boolean(id);
    },
    queryFn: () => {
      const id = toValue(spotId);
      if (!id) throw new Error('Missing spotId');
      return yardSpotService.getOneById(id) as Promise<TQueryFnData>;
    }
  });
}

/**
 * Query spots by warehouse ID.
 *
 * @param warehouseId - The warehouse ID to query spots for.
 *   Accepts MaybeRefOrGetter<string> OR { value: string } (ref-like object).
 *   The latter is needed because Pinia stores return refs with structural typing
 *   ({ value: T; [RefSymbol]: true }) rather than the nominal Ref<T> type that
 *   MaybeRefOrGetter expects. This is a TypeScript limitation where Pinia's type
 *   extraction doesn't preserve the exact Ref<T> type annotation.
 *   Since toValue() handles both at runtime, this union type allows direct
 *   usage of Pinia store refs without wrapping them in computed().
 */
export function useSpotsByWarehouseId<
  TQueryFnData = ISpot[],
  TError = unknown,
  TData = TQueryFnData
>(
  warehouseId:
    | MaybeRefOrGetter<WarehouseId | null | undefined>
    | { value: WarehouseId | null | undefined },
  params?: MaybeRefOrGetter<Record<string, unknown>>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'> & {
    enabled?: MaybeRefOrGetter<boolean>;
    joins?: MaybeRefOrGetter<string[]>;
  }
) {
  const joins = computed(
    () =>
      toValue(options?.joins) || [
        'spotAssignments',
        'spotAssignments.assetVisit',
        'spotAssignments.assetVisit.assetContainers',
        'spotAssignments.assetContainer'
      ]
  );

  return useQuery<TQueryFnData, TError, TData>({
    ...options,
    queryKey: computed(() => {
      const id = toValue(warehouseId as MaybeRefOrGetter<WarehouseId | null | undefined>);
      return yardAssetKeys.spots.byWarehouse(id || '', toValue(params), toValue(joins));
    }),
    enabled: () => {
      const id = toValue(warehouseId as MaybeRefOrGetter<WarehouseId | null | undefined>);
      const externalEnabled = toValue(
        (options?.enabled as MaybeRefOrGetter<boolean> | undefined) ?? true
      );
      return externalEnabled && Boolean(id);
    },
    queryFn: async () => {
      const id = toValue(warehouseId as MaybeRefOrGetter<WarehouseId | null | undefined>);
      const queryParams = toValue(params) || {};
      if (!id) throw new Error('Missing warehouseId');
      return yardSpotService.getManyByWarehouseId(id, queryParams, {
        joins: joins.value
      }) as Promise<TQueryFnData>;
    }
  });
}
